import React, { useEffect, useState, useRef } from "react";
import { motion } from "framer-motion";
import { FaGithub } from "react-icons/fa";
import hdwallet from "../assets/finalhdwallet.png";
import clothify from "../assets/clothify.png";
import landing from "../assets/coinflip.png";

const Projects = () => {
  const projectRefs = useRef([]); // Array of refs for each project
  const headingRef = useRef(null); // Ref for the heading
  const [visibleProjects, setVisibleProjects] = useState([]); // Track visible projects
  const [headingVisible, setHeadingVisible] = useState(false); // Track heading visibility

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const projectIndex = entry.target.dataset.index; // Get the project index
          if (entry.isIntersecting) {
            if (entry.target === headingRef.current) {
              setHeadingVisible(true);
            } else {
              setVisibleProjects((prevVisibleProjects) => [
                ...prevVisibleProjects,
                projectIndex,
              ]);
            }
            observer.unobserve(entry.target); // Stop observing once visible
          }
        });
      },
      {
        root: null, // viewport
        rootMargin: "0px", // No extra margins
        threshold: 0.5, // Trigger when 50% of the element is visible
      }
    );

    if (headingRef.current) observer.observe(headingRef.current); // Observe the heading

    projectRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref); // Observe each project
    });

    return () => {
      if (headingRef.current) observer.unobserve(headingRef.current);
      projectRefs.current.forEach((ref) => {
        if (ref) observer.unobserve(ref);
      });
    };
  }, []);

  const imageVariant = {
    hidden: { opacity: 0, x: -100 }, // Start from the left
    visible: { opacity: 1, x: 0, transition: { duration: 0.8 } },
  };

  const textVariant = {
    hidden: { opacity: 0, y: 50 }, // Start from below
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, delay: 0.2 } },
  };

  const headingVariant = {
    hidden: { opacity: 0, y: -50 }, // Start from above
    visible: { opacity: 1, y: 0, transition: { duration: 1 } },
  };

  return (
    <div className="mx-12 sm:px-16 md:border-b sm:mx-24">
      <motion.h1
        className="myProjects font-extrabold mb-8 sm:text-5xl lg:text-7xl"
        ref={headingRef}
        variants={headingVariant}
        initial="hidden"
        animate={headingVisible ? "visible" : "hidden"}
      >
        Featured Projects
      </motion.h1>
      <div className="">
        {projects.map((project, index) => (
          <div
            key={index}
            className="mb-10 py-5 md:flex justify-between"
            ref={(el) => (projectRefs.current[index] = el)}
            data-index={index}
          >
            <div className="relative group w-full md:w-1/2 aspect-w-16 aspect-h-9">
              <a
                href={project.link}
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full h-full"
              >
                {/* Aspect ratio container */}
                <motion.img
                  className="w-full h-full object-cover" // Ensure the image fits the container
                  src={project.img}
                  alt="illustration"
                  variants={imageVariant}
                  initial="hidden"
                  animate={visibleProjects.includes(String(index)) ? "visible" : "hidden"}
                />
                {/* Hover overlay */}
                <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <span className="text-white text-lg font-bold">View Project</span>
                </div>
              </a>
            </div>
            <div className="mt-4 md:mt-0 md:ml-8 w-full md:w-1/2">
              <motion.h3
                className="text-xl font-extrabold font-mono leading-8 pt-8 pb-2 sm:text-2xl md:pt-3 lg:text-3xl"
                variants={textVariant}
                initial="hidden"
                animate={
                  visibleProjects.includes(String(index)) ? "visible" : "hidden"
                }
              >
                {project.title}
              </motion.h3>
              <motion.p
                className="text-gray-400 font-mono text-sm mb-5 lg:text-lg"
                variants={textVariant}
                initial="hidden"
                animate={
                  visibleProjects.includes(String(index)) ? "visible" : "hidden"
                }
              >
                {project.description}
              </motion.p>
              <a
                href={project.github_link}
                target="_blank"
                rel="noopener noreferrer"
                className="block"
              >
                <motion.button
                  className="my-2"
                  variants={textVariant}
                  initial="hidden"
                  animate={visibleProjects.includes(String(index)) ? "visible" : "hidden"}
                >
                  <FaGithub size={24} />
                </motion.button>
              </a>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const projects = [
  {
    img: hdwallet,
    title: "Mnemonics Wallet",
    description:
      "It is a platform where user can generate public-private keypair from single seed phrase for Solana.",
    link: "https://hd-wallet-zeta.vercel.app/",
    github_link: "https://github.com/Bhushan-04/HD-Wallet"
  },
  {
    img: clothify,
    title: "Clothify",
    description:
      "Website that enables businesses to sell products and services online efficiently. The platform should provide a seamless shopping experience, including product browsing, selection, and secure checkout.",
    link: "https://clothify-f4851.web.app/",
    github_link: "https://github.com/Bhushan-04/Clothify"
  },
  {
    img: landing,
    title: "Landing Page",
    description:
      "Created a sleek and effective landing page to boost lead capture and user engagement. The page is designed to be visually appealing",
    link: "https://accredian-frontend-task-dusky-eight.vercel.app/",
    github_link: "https://github.com/Bhushan-04/Accredian-frontend-task"
  },
];

export default Projects;
